import React, { useEffect, useRef, useState } from 'react';
import styles from './locateServices.module.scss';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { classNames } from '@/core/utils/class.utils';
import { openUrlInNewTab } from '@/core/utils/general.utils';
import { useDashboard } from '@/contexts/dashboard/dashboard.context';
import { renderCards } from '../../helper';
import { PrimaryButton } from '@/design-system/buttons/primary-button/primary-button';
import { BodyS } from '@/design-system/text/bodyS/bodyS';
import { H6 } from '@/design-system/text/h6/h6';

const LocateServices = () => {
  const { dashboardLabels } = useDashboard();
  const data = renderCards(dashboardLabels.locateServices.cards);
  const divRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  useEffect(() => {
    const currElement = divRefs.current.get(selectedIndex.toString());
    if (currElement && scrollRef.current) {
      scrollRef.current.scrollTo({
        behavior: 'smooth',
        left: currElement.offsetLeft - scrollRef.current.offsetLeft,
      });
    }
  }, [selectedIndex]);

  if (data.length === 0) return <></>;

  const scrollToPrevious = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const scrollToNext = () => {
    if (selectedIndex < data.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  return (
    <div className={styles['locate-hsp-con']}>
      <div className={styles['section-con']}>
        <IconChevronLeft
          size={18}
          onClick={scrollToPrevious}
          className={classNames(styles['arrow'], {
            [styles['disabled']]: selectedIndex === 0,
          })}
        />
        <div className={styles['sections']} ref={scrollRef}>
          {data.map((section, index) => {
            return (
              <div
                ref={(el) => {
                  if (el) {
                    divRefs.current.set(index.toString(), el);
                  }
                }}
                onClick={() => {
                  setSelectedIndex(index);
                }}
                key={index}
              >
                <BodyS
                  text={section.sectionName}
                  className={classNames(styles['section-title'], {
                    [styles['selected']]: selectedIndex === index,
                  })}
                />
              </div>
            );
          })}
        </div>
        <IconChevronRight
          size={18}
          onClick={scrollToNext}
          className={classNames(styles['arrow'], {
            [styles['disabled']]: selectedIndex === data.length - 1,
          })}
        />
      </div>
      <div className={styles['title-desc-con']}>
        <H6 text={data[selectedIndex].title} />
        <BodyS
          text={data[selectedIndex].description}
          className={styles['desc']}
        />
      </div>
      <div className={styles['btn-con']}>
        <PrimaryButton
          id="locate-services-button"
          onClick={() => openUrlInNewTab(data[selectedIndex].redirectionLink)}
          clevertapEventPayload={
            dashboardLabels.locateServices.buttonTextClevertapEvent?.payload
          }
          clevertapEventName={
            dashboardLabels.locateServices.buttonTextClevertapEvent?.name
          }
          text={data[selectedIndex].btnText}
        />
      </div>
    </div>
  );
};

export default LocateServices;
